import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Col, Div, Icon, Row, Text } from '@pinpoint/design-kit';
import { charts } from '@pinpoint/onpoint-charts';
import { Button } from 'src/components';
import CONSTANTS from 'src/store/constants';
import { ModelProp } from 'src/store/typings';

const formatNumberLabel = (data) => {
  let { value: num } = data;
  if (typeof num === 'string') num = Number(num);
  if (!num || !Number.isFinite(num)) return Number.NaN;
  return num.toFixed(2);
};

export default function CompareModels({
  modelsCompare: {
    models,
    chart,
    quantiles,
    quantileIndices,
    liftScores: perModelLiftScores,
    availableQuantiles: { current },
  },
  setQuantiles,
  setChart,
}) {
  const [theme, setTheme] = useState('pinpoint');
  const selectedLiftScores = useMemo(
    () => perModelLiftScores.map((lftScs, idx) => lftScs[quantileIndices[idx]]),
    [perModelLiftScores, quantileIndices]
  );
  const axisLabelInts = useMemo(
    () =>
      selectedLiftScores
        ?.map?.((lfs) => lfs.percentile)
        ?.reduce?.((acc, cur) => [...acc, ...cur], [])
        ?.every?.((pn) => Number.isSafeInteger(pn)) ?? false,
    [selectedLiftScores]
  );

  const tornadoLifts = useMemo(
    () => selectedLiftScores?.map(({ lift = [] }) => lift.map((x) => (x >= 1 ? x : -1 / x))) || [],
    [selectedLiftScores]
  );

  const lineSeries = useMemo(
    () =>
      selectedLiftScores?.length
        ? models.map((model, index) => ({
            name: model.name,
            values: selectedLiftScores[index].lift,
            data: selectedLiftScores[index].lift,
            type: 'line',
            lineStyle: { width: 3 },
            label: { show: true, position: 'top', formatter: formatNumberLabel },
          }))
        : [],
    [models, selectedLiftScores]
  );

  const vertBarChart = useMemo(
    () =>
      selectedLiftScores?.length
        ? {
            series: models.map((model, index) => ({
              name: model.name,
              values: selectedLiftScores[index].lift,
            })),
            spreads: models.map(
              (model, index) =>
                selectedLiftScores[index].lift[selectedLiftScores[index].lift.length - 1] /
                selectedLiftScores[index].lift[0]
            ),
          }
        : { series: [], spreads: [] },
    [models, selectedLiftScores]
  );

  const tornadoChart = useMemo(
    () => ({
      series: models.map((model, index) => ({
        name: model.name,
        values: tornadoLifts[index],
      })),
      options: {
        grid: {
          bottom: 120,
        },
        xAxis: {
          name: 'Score percentile buckets, segmenting validation set',
          axisLabel: {
            formatter: (num) => (+num).toFixed(+!axisLabelInts),
          },
        },
        yAxis: {
          name: 'Likelihood of Positive Engagement',
        },
        tooltip: {
          formatter: ({ seriesIndex, dataIndex }) => {
            const likelihood = tornadoLifts[seriesIndex][dataIndex];
            const percentile = selectedLiftScores[seriesIndex].percentile[dataIndex];
            const width = 100 / selectedLiftScores[seriesIndex].percentile.length;
            return `
              <div style="padding: 8px;">
                Percentile bucket: ${(percentile - width).toFixed(+!axisLabelInts)} - ${percentile.toFixed(
              +!axisLabelInts
            )}
                <br />
                Likelihood: ${likelihood.toFixed(1)}X
              </div>
              `;
          },
        },
        legend: {
          textSize: 16,
          itemGap: 24,
          textStyle: {
            fontSize: 16,
          },
        },
      },
    }),
    [models, tornadoLifts, axisLabelInts, selectedLiftScores]
  );

  if (selectedLiftScores?.length)
    return (
      <Div h="100%" w="100%" p={{ x: '1.5rem', y: '1rem' }} d="flex" flexDir="column">
        <Row data-test="title-row">
          <Col data-test="title-btns-col" gutterWidth="0" size="6" d="flex" align="center" justify="flex-start">
            <Text textSize="title" textColor="tabSelected" style={{ fontWeight: 400 }} p={{ r: '1.5rem' }}>
              Lift Calculations
            </Text>
            {current.map((qn) => (
              <Button
                key={`button-${qn}`}
                buttonStyle="tab"
                p={{ x: '1.5em' }}
                w="1.875rem"
                selected={quantiles === qn}
                onClick={() => {
                  setQuantiles(qn);
                }}
              >
                {qn}
              </Button>
            ))}
          </Col>
          <Col data-test="title-btns-col" gutterWidth="0" size="6" justify="flex-end" align="center" d="flex">
            <Button
              buttonStyle="tab"
              selected={chart === CONSTANTS.TAL_MODEL.CHARTS.TORNADO_BAR_CHART}
              p={{ x: '1rem' }}
              onClick={() => {
                setChart(CONSTANTS.TAL_MODEL.CHARTS.TORNADO_BAR_CHART);
              }}
            >
              <Icon name="VerticalBarChart" color="white" />
            </Button>
            <Button
              buttonStyle="tab"
              selected={chart === CONSTANTS.TAL_MODEL.CHARTS.BAR_CHART}
              p={{ x: '1rem' }}
              onClick={() => {
                setChart(CONSTANTS.TAL_MODEL.CHARTS.BAR_CHART);
              }}
            >
              <Icon
                name="BarChart"
                color="white"
                style={{
                  transform: 'rotate(270deg) scale(0.8, -0.8)',
                }}
              />
            </Button>
            <Button
              buttonStyle="tab"
              selected={chart === CONSTANTS.TAL_MODEL.CHARTS.LINE_CHART}
              m={{ r: '0rem' }}
              p={{ x: '1rem' }}
              onClick={() => {
                setChart(CONSTANTS.TAL_MODEL.CHARTS.LINE_CHART);
              }}
            >
              <Icon name="LineChart" color="white" />
            </Button>
          </Col>
        </Row>
        <Row data-test="chart-row" p={{ t: '.5rem' }} flexGrow="1">
          <Col data-test="chart-content-col" gutterWidth="0" align="center" justify="center" d="flex" size="12">
            {
              {
                [CONSTANTS.TAL_MODEL.CHARTS.LINE_CHART]: (
                  <charts.Line
                    labels={selectedLiftScores[0].percentile}
                    series={lineSeries}
                    options={{
                      grid: {
                        bottom: 120,
                      },
                      tooltip: {
                        formatter: ({ dataIndex, seriesIndex }) => {
                          const likelihood = Number(selectedLiftScores[seriesIndex]?.lift?.[dataIndex]);
                          const endVal = +(selectedLiftScores[seriesIndex]?.percentile?.[dataIndex] ?? 0);
                          const endValIdx = selectedLiftScores[seriesIndex].percentile.findIndex(
                            (val) => val === +endVal
                          );
                          const startVal = selectedLiftScores[seriesIndex].percentile?.[endValIdx - 1] ?? 0;
                          return `Percentile bucket: ${startVal.toFixed(+!axisLabelInts)} - ${endVal.toFixed(
                            +!axisLabelInts
                          )} <br /> Lift: ${likelihood.toFixed(1)}`;
                        },
                      },
                      xAxis: {
                        name: 'Score percentile bucket for validation set',
                        nameGap: 40,
                        axisLabel: {
                          formatter: (num) => (+num).toFixed(+!axisLabelInts),
                        },
                      },
                      legend: {
                        padding: [0, 0, 20, 0],
                        itemHeight: 16,
                        itemWidth: 16,
                        itemSize: 16,
                        itemGap: 24,
                        textStyle: {
                          fontSize: 16,
                        },
                      },
                    }}
                    theme={theme}
                    defaultTheme="pinpoint"
                    onThemeChange={(nextTheme) => {
                      setTheme(nextTheme);
                    }}
                    usePalette
                  />
                ),
                [CONSTANTS.TAL_MODEL.CHARTS.BAR_CHART]: (
                  <charts.VerticalBar
                    labels={selectedLiftScores[0].percentile}
                    series={vertBarChart.series}
                    options={{
                      grid: {
                        bottom: 120,
                      },
                      tooltip: {
                        formatter: ({ dataIndex, seriesIndex }) => {
                          const likelihood = Number(selectedLiftScores[seriesIndex]?.lift?.[dataIndex]);
                          const endVal = +(selectedLiftScores[seriesIndex]?.percentile?.[dataIndex] ?? 0);
                          const endValIdx = selectedLiftScores[seriesIndex].percentile.findIndex(
                            (val) => val === +endVal
                          );
                          const startVal = selectedLiftScores[seriesIndex].percentile?.[endValIdx - 1] ?? 0;
                          return `Percentile bucket: ${startVal.toFixed(+!axisLabelInts)} - ${endVal.toFixed(
                            +!axisLabelInts
                          )} <br /> Lift: ${likelihood.toFixed(1)}`;
                        },
                      },
                      xAxis: {
                        name: 'Score percentile buckets, segmenting validation set',
                        axisLabel: {
                          formatter: (num) => (+num).toFixed(+!axisLabelInts),
                        },
                      },
                      legend: {
                        textSize: 16,
                        itemGap: 24,
                        textStyle: {
                          fontSize: 16,
                        },
                      },
                    }}
                    theme={theme}
                    onThemeChange={(nextTheme) => {
                      setTheme(nextTheme);
                    }}
                    usePalette
                    spreads={vertBarChart.spreads}
                  />
                ),
                [CONSTANTS.TAL_MODEL.CHARTS.TORNADO_BAR_CHART]: (
                  <charts.VerticalBar
                    labels={selectedLiftScores[0].percentile}
                    series={tornadoChart.series}
                    options={tornadoChart.options}
                    markLine={false}
                    formatter={(x) => `${x.value.toFixed(1)}X`}
                    theme={theme}
                    onThemeChange={(nextTheme) => {
                      setTheme(nextTheme);
                    }}
                    usePalette
                    spreads={models
                      .filter((model, index) => selectedLiftScores[index].lift)
                      .map((model, index) =>
                        selectedLiftScores[index].lift
                          ? selectedLiftScores[index].lift[selectedLiftScores[index].lift.length - 1] /
                            selectedLiftScores[index].lift[0]
                          : 0
                      )}
                  />
                ),
                default: null,
              }[chart]
            }
          </Col>
        </Row>
      </Div>
    );
  return null;
}

CompareModels.propTypes = {
  modelsCompare: PropTypes.shape({
    models: PropTypes.arrayOf(ModelProp).isRequired,
    fetchModelsStatus: PropTypes.oneOf([PropTypes.string, null]).isRequired,
    chart: PropTypes.string,
    availableQuantiles: PropTypes.shape({ current: PropTypes.arrayOf(PropTypes.number) }).isRequired,
    quantiles: PropTypes.number.isRequired,
    quantileIndices: PropTypes.arrayOf(PropTypes.number).isRequired,
    liftScores: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }).isRequired,
  setQuantiles: PropTypes.func.isRequired,
  setChart: PropTypes.func.isRequired,
};
